/**
 * Track a DOM element's bounding rect, updating on scroll, resize, and
 * resize-observer signals. rAF-coalesced so we never render more than
 * once per frame even when multiple sources fire simultaneously.
 */
import { useEffect, useState } from "react";
const measure = (el) => el.getBoundingClientRect();
export const useElementRect = (element) => {
    const [rect, setRect] = useState(() => element ? measure(element) : null);
    useEffect(() => {
        if (!element) {
            setRect(null);
            return;
        }
        let frame = 0;
        const schedule = () => {
            if (frame !== 0)
                return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                setRect(measure(element));
            });
        };
        schedule();
        const ro = new ResizeObserver(schedule);
        ro.observe(element);
        window.addEventListener("scroll", schedule, {
            passive: true,
            capture: true,
        });
        window.addEventListener("resize", schedule, { passive: true });
        return () => {
            if (frame !== 0)
                cancelAnimationFrame(frame);
            ro.disconnect();
            window.removeEventListener("scroll", schedule, {
                capture: true,
            });
            window.removeEventListener("resize", schedule);
        };
    }, [element]);
    return rect;
};
export const useElementRects = (elements) => {
    const [rects, setRects] = useState(() => elements.map(measure));
    useEffect(() => {
        if (elements.length === 0) {
            setRects([]);
            return;
        }
        let frame = 0;
        const schedule = () => {
            if (frame !== 0)
                return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                setRects(elements.map(measure));
            });
        };
        schedule();
        const ro = new ResizeObserver(schedule);
        for (const el of elements)
            ro.observe(el);
        window.addEventListener("scroll", schedule, {
            passive: true,
            capture: true,
        });
        window.addEventListener("resize", schedule, { passive: true });
        return () => {
            if (frame !== 0)
                cancelAnimationFrame(frame);
            ro.disconnect();
            window.removeEventListener("scroll", schedule, {
                capture: true,
            });
            window.removeEventListener("resize", schedule);
        };
    }, [elements]);
    return rects;
};
//# sourceMappingURL=useElementRect.js.map